import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

/** The API's page size — DRF `PAGE_SIZE` in `common.pagination.PageSizePagination`.
 *
 * This footer only *reports* the window ("11–20 of 43"); it does not choose it.
 * When the two disagree the count is quietly wrong, which is how this sat at 10
 * against a backend of 20 and printed "1–10 of 43" beneath twenty rows. */
export const DEFAULT_PAGE_SIZE = 20;

interface ListPaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total row count (DRF `count`). */
  count: number;
  /** Rows per page. Defaults to {@link DEFAULT_PAGE_SIZE}; pass it only when
   *  the query overrides `page_size`. */
  pageSize?: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPageChange: (page: number) => void;
}

/** DRF page-number pagination footer: "from–to of count" + prev/next. */
export function ListPagination({
  page,
  count,
  pageSize = DEFAULT_PAGE_SIZE,
  hasPrevious,
  hasNext,
  onPageChange,
}: ListPaginationProps) {
  const { t } = useTranslation('common');
  if (count === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, count);

  return (
    <div className="flex items-center justify-between pt-2">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t('pagination.showing', { from, to, count })}
      </p>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          disabled={!hasPrevious}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('pagination.previous')}
        >
          <ChevronLeft />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('pagination.next')}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
